import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const blank = () => ({ name: "New Gradient", from_color: "#FF512F", to_color: "#F09819", angle: 90, enabled: true, order: 99 });

export default function GradientsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.adminGradients().then((d) => setRows(d.gradients || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (i, k, v) => setRows((r) => r.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  const save = async (g) => {
    setSaving(g.id || g.name);
    try { await api.saveGradient(g); toast.success(`Saved "${g.name}"`); load(); }
    catch { toast.error("Could not save gradient."); }
    finally { setSaving(""); }
  };
  const remove = async (g) => {
    if (!g.id) return;
    try { await api.deleteGradient(g.id); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete."); }
  };
  // Drag-to-reorder: on drop, renumber `order` and persist every changed row so the
  // editor's Popular gradient order matches immediately.
  const onDrop = async (to) => {
    if (dragIdx === null || dragIdx === to) { setDragIdx(null); return; }
    const next = [...rows];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(to, 0, moved);
    const renumbered = next.map((g, i) => ({ ...g, order: i }));
    setRows(renumbered); setDragIdx(null);
    try {
      await Promise.all(renumbered.filter((g) => g.id).map((g) => api.saveGradient(g)));
      toast.success("Order updated");
    } catch { toast.error("Could not save new order."); load(); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0e0f11] p-4" data-testid="gradients-admin">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-bold text-white">Popular Gradients</h3>
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-[#A1A1AA]">shown in the editor's Fill panel</span>
        <button data-testid="grad-add" onClick={() => setRows((r) => [...r, blank()])}
          className="ml-auto flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white hover:border-[#00F0FF]/50">
          <Plus size={13} /> Add gradient
        </button>
      </div>
      {loading && <Loader2 className="mx-auto animate-spin text-[#a78bfa]" />}
      <div className="space-y-2">
        {rows.map((g, i) => (
          <div key={g.id || i} data-testid={`grad-row-${i}`} draggable
            onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
            className={`flex flex-wrap items-center gap-2 rounded-lg border bg-black/30 p-2 ${dragIdx === i ? "border-[#00F0FF] opacity-60" : "border-white/10"}`}>
            <GripVertical size={14} className="cursor-grab text-[#555]" />
            <div className="h-8 w-16 rounded-md border border-white/10" style={{ backgroundImage: `linear-gradient(${g.angle ?? 90}deg, ${g.from_color}, ${g.to_color})` }} />
            <input value={g.name} onChange={(e) => patch(i, "name", e.target.value)} data-testid={`grad-name-${i}`}
              className="w-32 rounded border border-white/10 bg-[#1a1a1a] px-2 py-1 text-xs text-white outline-none" />
            <input type="color" value={g.from_color} onChange={(e) => patch(i, "from_color", e.target.value)} data-testid={`grad-from-${i}`} className="h-7 w-7 rounded border-0 bg-transparent p-0" />
            <input type="color" value={g.to_color} onChange={(e) => patch(i, "to_color", e.target.value)} data-testid={`grad-to-${i}`} className="h-7 w-7 rounded border-0 bg-transparent p-0" />
            <label className="flex items-center gap-1 text-[10px] text-[#A1A1AA]">Angle
              <input type="number" value={g.angle ?? 90} onChange={(e) => patch(i, "angle", parseInt(e.target.value) || 0)} className="w-14 rounded border border-white/10 bg-[#1a1a1a] px-1 py-1 text-xs text-white outline-none" /></label>
            <label className="flex items-center gap-1 text-[10px] text-[#A1A1AA]">Order
              <input type="number" value={g.order ?? 0} onChange={(e) => patch(i, "order", parseInt(e.target.value) || 0)} className="w-12 rounded border border-white/10 bg-[#1a1a1a] px-1 py-1 text-xs text-white outline-none" /></label>
            <label className="flex items-center gap-1 text-[10px] text-[#A1A1AA]">
              <input type="checkbox" checked={g.enabled !== false} onChange={(e) => patch(i, "enabled", e.target.checked)} data-testid={`grad-enabled-${i}`} /> Enabled</label>
            <div className="ml-auto flex gap-1">
              <button onClick={() => save(g)} disabled={saving === (g.id || g.name)} data-testid={`grad-save-${i}`}
                className="rounded border border-[#00F0FF]/40 px-2 py-1 text-[11px] font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-50">
                {saving === (g.id || g.name) ? "…" : "Save"}</button>
              <button onClick={() => remove(g)} data-testid={`grad-delete-${i}`} className="rounded border border-white/10 px-2 py-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
